import React, { useCallback, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { useMaxGalleriesPerEvent } from "../hooks/useSystemSetting";
import {
  useGetEventByIdQuery,
  useGetEventGalleriesQuery,
  useCreateGalleryMutation
} from "../services/nestjs-base-api";
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  Layout,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Typography,
  message,
  Popover,
  QRCode,
  Tooltip
} from "antd";
import {
  PlusOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  PictureOutlined,
  LinkOutlined,
  QrcodeOutlined,
  EyeInvisibleOutlined,
  ArrowLeftOutlined
} from "@ant-design/icons";
import { FormattedMessage, useIntl } from "react-intl";
import { UserRoleEnum } from "../enums/UserRoleEnum";
import { useNavigate, useParams } from "react-router-dom";
import { GalleryTypeEnum } from "../enums/GalleryTypeEnum";
import { useCopyToClipboard } from "react-use";
import { UpgradeModal } from "../components/UpgradeModal";
import RecognitionIndexingStatus from "../components/RecognitionIndexingStatus";
import EventAnalytics from "../components/EventAnalytics";
import styled from "styled-components";

const { Title, Text, Paragraph } = Typography;
const { Content } = Layout;
const { TextArea } = Input;
const { Meta } = Card;

interface IGalleryFormValues {
  name: string;
  description?: string;
  type: string;
}

const StyledContent = styled(Content)`
  margin: 0 24px;
  
  @media (max-width: 768px) {
    margin: 0 16px;
  }
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  margin: 24px 0 16px 0;
  
  .ant-breadcrumb-link {
    color: #666 !important;
    
    &:hover {
      color: #4169e1 !important;
    }
  }
  
  a {
    color: #4169e1 !important;
    text-decoration: none !important;
    
    &:hover {
      color: #1890ff !important;
    }
  }
`;

const ContentWrapper = styled.div`
  padding: 32px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  min-height: calc(100vh - 200px);
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const EventHeaderCard = styled(Card)`
  margin-bottom: 32px;
  border-radius: 12px;
  border: 1px solid #f0f0f0;
  
  .ant-card-body {
    padding: 32px !important;
    
    @media (max-width: 768px) {
      padding: 20px !important;
    }
  }
`;

const StyledTitle = styled(Title)`
  margin: 0 0 16px 0 !important;
  color: #1a2847;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  
  .anticon {
    color: #4169e1 !important;
    margin-right: 8px !important;
    font-size: 16px !important;
  }
`;

const StyledButton = styled(Button)`
  &.ant-btn-primary {
    background: linear-gradient(135deg, #4169e1 0%, #1890ff 100%) !important;
    border: none !important;
    height: 40px !important;
    padding: 0 24px !important;
    font-size: 15px !important;
    font-weight: 500 !important;
    box-shadow: 0 4px 12px rgba(65, 105, 225, 0.3) !important;
    transition: all 0.3s ease !important;
    
    &:hover {
      transform: translateY(-1px) !important;
      box-shadow: 0 6px 16px rgba(65, 105, 225, 0.4) !important;
    }
  }
  
  &.ant-btn-default {
    height: 40px !important;
    padding: 0 20px !important;
    border-color: #e6e6e6 !important;
    
    &:hover {
      border-color: #4169e1 !important;
      color: #4169e1 !important;
    }
  }
`;

const SectionTitle = styled(Title)`
  margin: 0 0 24px 0 !important;
  color: #1a2847;
`;

const GalleryCard = styled(Card)`
  height: 100%;
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    border-color: #e6f7ff;
  }
  
  .ant-card-cover {
    background: linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%) !important;
    height: 140px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    
    .anticon {
      font-size: 48px !important;
      color: #4169e1 !important;
    }
  }
  
  .ant-card-meta-title {
    color: #1a2847 !important;
    font-size: 16px !important;
    font-weight: 600 !important;
    display: flex !important;
    align-items: center !important;
  }
`;

const EmptyStateWrapper = styled.div`
  padding: 60px 0;
  text-align: center;
`;

const StyledModal = styled(Modal)`
  .ant-modal-header {
    border-bottom: 1px solid #f0f0f0 !important;
  }
  
  .ant-modal-title {
    color: #1a2847 !important;
    font-size: 18px !important;
    font-weight: 600 !important;
  }
  
  .ant-form-item-label > label {
    color: #666 !important;
    font-weight: 500 !important;
  }
  
  .ant-input,
  .ant-input-textarea,
  .ant-select-selector {
    border-radius: 8px !important;
    
    &:hover {
      border-color: #4169e1 !important;
    }
  }
`;

const LoadingWrapper = styled.div`
  text-align: center;
  padding: 100px 0;
`;

const EventDetailPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const [form] = Form.useForm<IGalleryFormValues>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [, copyToClipboard] = useCopyToClipboard();
  
  // Get max galleries per event limit from system settings
  const maxGalleriesPerEvent = useMaxGalleriesPerEvent("1");

  // Check if user is org admin
  const isOrgAdmin = user?.roles?.includes(UserRoleEnum.OrganisationAdmin) ||
    user?.roles?.includes(UserRoleEnum.SuperAdmin);

  // Get event details
  const {
    data: event,
    error: eventError,
    isLoading: isLoadingEvent
  } = useGetEventByIdQuery({
    jwtToken: user.jwtToken,
    eventId: Number(eventId)
  }, { skip: !eventId });

  // Get event galleries
  const {
    data: galleries,
    error: galleriesError,
    isLoading: isLoadingGalleries,
    refetch: refetchGalleries
  } = useGetEventGalleriesQuery({
    jwtToken: user.jwtToken,
    eventId: Number(eventId)
  }, { skip: !eventId });

  // Create gallery mutation
  const [createGallery, { isLoading: isCreating }] = useCreateGalleryMutation();

  // Handle create gallery submit
  const handleCreateGallery = useCallback(async (values: IGalleryFormValues) => {
    if (!eventId) return;

    try {
      await createGallery({
        jwtToken: user.jwtToken,
        payload: {
          name: values.name,
          description: values.description,
          eventId: Number(eventId),
          type: values.type
        }
      }).unwrap();

      messageApi.success(intl.formatMessage({ id: 'gallery.createSuccess' }) || 'Gallery created successfully');
      setIsModalOpen(false);
      form.resetFields();
      refetchGalleries();
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        logout();
      } else {
        messageApi.error(intl.formatMessage({ id: 'gallery.createError' }) || 'Error creating gallery');
      }
    }
  }, [createGallery, eventId, form, intl, logout, messageApi, refetchGalleries, user.jwtToken]);

  // Check if user can create more galleries
  const canCreateMoreGalleries = useCallback(() => {
    if (!galleries) return true; // If galleries data isn't loaded yet, allow creation
    
    const maxGalleriesCount = parseInt(maxGalleriesPerEvent, 10) || 1;
    return galleries.length < maxGalleriesCount;
  }, [galleries, maxGalleriesPerEvent]);
  
  // Show modal for creating gallery or show upgrade modal
  const showModal = () => {
    if (canCreateMoreGalleries()) {
      form.resetFields();
      form.setFieldsValue({
        type: GalleryTypeEnum.General
      });
      setIsModalOpen(true);
    } else {
      setIsUpgradeModalOpen(true);
    }
  };
  
  // Handle close upgrade modal
  const handleCloseUpgradeModal = () => {
    setIsUpgradeModalOpen(false);
  };

  // Handle cancel modal
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // Navigate to gallery details
  const navigateToGalleryDetails = useCallback((galleryId: number) => {
    navigate(`/dashboard/events/${eventId}/galleries/${galleryId}`);
  }, [navigate, eventId]);

  // Return to events list
  const handleBackToEvents = useCallback(() => {
    navigate('/dashboard/events');
  }, [navigate]);
  
  // Copy event URL to clipboard
  const copyEventUrl = useCallback((hash: string) => {
    const baseUrl = window.location.origin;
    const eventUrl = `${baseUrl}/event/${hash}`;
    copyToClipboard(eventUrl);
    messageApi.success(intl.formatMessage({ id: 'events.urlCopied' }) || 'Event URL copied to clipboard');
  }, [copyToClipboard, intl, messageApi]);
  
  // Check for errors
  React.useEffect(() => {
    if (eventError || galleriesError) {
      if (
        (eventError && 'status' in eventError && eventError.status === 401) ||
        (galleriesError && 'status' in galleriesError && galleriesError.status === 401)
      ) {
        logout();
      }
    }
  }, [eventError, galleriesError, logout]);

  // Loading state
  if (isLoadingEvent) {
    return (
      <StyledContent>
        <LoadingWrapper>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 16, color: '#666' }}>
            {intl.formatMessage({ id: 'event.loading' }) || 'Loading event details...'}
          </Paragraph>
        </LoadingWrapper>
      </StyledContent>
    );
  }

  // Event not found
  if (!event && !isLoadingEvent) {
    return (
      <StyledContent>
        <ContentWrapper>
          <EmptyStateWrapper>
            <Empty
              description={intl.formatMessage({ id: 'event.notFound' }) || 'Event not found'}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
            <StyledButton type="primary" onClick={handleBackToEvents} style={{ marginTop: 16 }}>
              {intl.formatMessage({ id: 'events.backToList' }) || 'Back to Events'}
            </StyledButton>
          </EmptyStateWrapper>
        </ContentWrapper>
      </StyledContent>
    );
  }

  return (
    <StyledContent>
      {contextHolder}
      <StyledBreadcrumb>
        <Breadcrumb.Item>{user.organisation ? user.organisation.name : intl.formatMessage({ id: 'common.platformName' })}</Breadcrumb.Item>
        <Breadcrumb.Item>
          <a onClick={handleBackToEvents}><FormattedMessage id={'section.eventsName'} /></a>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{event?.name}</Breadcrumb.Item>
      </StyledBreadcrumb>

      <ContentWrapper>
        {/* Event Header */}
        <EventHeaderCard>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
            <div style={{ flex: 1, minWidth: '300px' }}>
              <StyledButton 
                icon={<ArrowLeftOutlined />} 
                onClick={handleBackToEvents}
                style={{ marginBottom: 16 }}
              >
                {intl.formatMessage({ id: 'common.back' }) || 'Back'}
              </StyledButton>
              
              <StyledTitle level={2}>{event?.name}</StyledTitle>
              
              <Space direction="vertical" size={12} style={{ marginBottom: 16, width: '100%' }}>
                <InfoItem>
                  <CalendarOutlined />
                  <Text style={{ color: '#666' }}>
                    {new Date(event?.date || '').toLocaleDateString(undefined, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Text>
                </InfoItem>
                
                {event?.location && (
                  <InfoItem>
                    <EnvironmentOutlined />
                    <Text style={{ color: '#666' }}>{event.location}</Text>
                  </InfoItem>
                )}
              </Space>
              
              {event?.description && (
                <Paragraph style={{ color: '#666', marginBottom: 20 }}>
                  {event.description}
                </Paragraph>
              )}
              
              <Space wrap>
                <Popover 
                  content={
                    <div style={{ textAlign: 'center' }}>
                      <QRCode
                        value={`${window.location.origin}/event/${event?.hash}`}
                        size={200}
                        bordered={false}
                        style={{ margin: '10px 0' }}
                        icon="/favicon.ico"
                      />
                      <Typography.Paragraph style={{ marginBottom: 8 }}>
                        {event?.name}
                      </Typography.Paragraph>
                      <StyledButton 
                        type="primary" 
                        size="small"
                        onClick={() => {
                          const canvas = document.querySelector('.ant-qrcode canvas') as HTMLCanvasElement;
                          if (canvas) {
                            const url = canvas.toDataURL('image/png');
                            const a = document.createElement('a');
                            a.download = `${event?.name} QR Code.png`;
                            a.href = url;
                            a.click();
                          }
                        }}
                      >
                        {intl.formatMessage({ id: 'events.qrcode.download' }) || 'Download QR Code'}
                      </StyledButton>
                    </div>
                  }
                  trigger="click"
                  title={intl.formatMessage({ id: 'events.qrcode' }) || 'Event QR Code'}
                >
                  <StyledButton type="default" icon={<QrcodeOutlined />}>
                    {intl.formatMessage({ id: 'events.table.qrcode' }) || 'QR Code'}
                  </StyledButton>
                </Popover>
                
                <StyledButton 
                  type="default" 
                  icon={<LinkOutlined />} 
                  onClick={() => event?.hash && copyEventUrl(event.hash)}
                >
                  {intl.formatMessage({ id: 'events.table.copyLink' }) || 'Copy Link'}
                </StyledButton>
              </Space>
            </div>
            
            {isOrgAdmin && (
              <StyledButton
                type="primary"
                icon={<PlusOutlined />}
                onClick={showModal}
              >
                {intl.formatMessage({ id: 'gallery.create' }) || 'Create Gallery'}
              </StyledButton>
            )}
          </div>
        </EventHeaderCard>

        {/* Event Analytics Section */}
        {isOrgAdmin && (
          <div style={{ marginBottom: 32 }}>
            <SectionTitle level={3}>
              {intl.formatMessage({ id: 'analytics.title' }) || 'Event Analytics'}
            </SectionTitle>
            <EventAnalytics eventId={Number(eventId)} />
          </div>
        )}

        {/* Photo Recognition Section */}
        {isOrgAdmin && (
          <Card 
            style={{ 
              marginBottom: 32, 
              borderRadius: 12, 
              border: '1px solid #f0f0f0' 
            }}
          >
            <RecognitionIndexingStatus 
              eventId={Number(eventId)} 
              eventName={event?.name || ''} 
            />
          </Card>
        )}

        {/* Galleries Section */}
        <div>
          <SectionTitle level={3}>
            {intl.formatMessage({ id: 'event.galleries' }) || 'Galleries'}
          </SectionTitle>
          
          {isLoadingGalleries ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" />
              <Paragraph style={{ marginTop: 16, color: '#666' }}>
                {intl.formatMessage({ id: 'gallery.loading' }) || 'Loading galleries...'}
              </Paragraph>
            </div>
          ) : galleries && galleries.length > 0 ? (
            <Row gutter={[24, 24]}>
              {galleries.map(gallery => (
                <Col xs={24} sm={12} md={8} lg={6} key={gallery.id}>
                  <GalleryCard
                    hoverable
                    cover={
                      <div
                          style={{
                            height: 140,
                            background: '#f0f5ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                      >
                        <PictureOutlined />
                      </div>
                    }
                    onClick={() => navigateToGalleryDetails(gallery.id)}
                  >
                    <Meta
                      title={
                        <span>
                          {gallery.name}
                          {gallery.excludeFromRecognition && (
                            <Tooltip title={intl.formatMessage({ id: 'gallery.recognitionDisabledTooltip' }) || 'Guests cannot find photos from this gallery using selfies'}>
                              <EyeInvisibleOutlined 
                                style={{ 
                                  marginLeft: 8, 
                                  fontSize: '14px', 
                                  color: '#ff7875' 
                                }} 
                              />
                            </Tooltip>
                          )}
                        </span>
                      }
                      description={
                        <div>
                          {gallery.description && (
                            <Paragraph 
                              ellipsis={{ rows: 2 }} 
                              style={{ marginBottom: 8, color: '#999' }}
                            >
                              {gallery.description}
                            </Paragraph>
                          )}
                          <Space direction="vertical" size={4}>
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                              {new Date(gallery.createdAt).toLocaleDateString()}
                            </Text>
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                              {intl.formatMessage({ id: `gallery.type.${gallery.type.toLowerCase()}` }) || gallery.type}
                            </Text>
                          </Space>
                        </div>
                      }
                    />
                  </GalleryCard>
                </Col>
              ))}
            </Row>
          ) : (
            <EmptyStateWrapper>
              <Empty 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span>
                    {intl.formatMessage({ id: 'gallery.empty' }) || 'No galleries found'}
                    {isOrgAdmin && (
                      <div style={{ marginTop: 16 }}>
                        <StyledButton type="primary" icon={<PlusOutlined />} onClick={showModal}>
                          {intl.formatMessage({ id: 'gallery.create' }) || 'Create Gallery'}
                        </StyledButton>
                      </div>
                    )}
                  </span>
                }
              />
            </EmptyStateWrapper>
          )}
        </div>
      </ContentWrapper>

      {/* Create Gallery Modal */}
      <StyledModal
        title={intl.formatMessage({ id: 'gallery.createTitle' }) || 'Create New Gallery'}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateGallery}
        >
          <Form.Item
            name="name"
            label={intl.formatMessage({ id: 'gallery.form.name' }) || 'Gallery Name'}
            rules={[{ required: true, message: intl.formatMessage({ id: 'gallery.form.nameRequired' }) || 'Please enter gallery name' }]}
          >
            <Input placeholder={intl.formatMessage({ id: 'gallery.form.namePlaceholder' }) || 'Enter gallery name'} />
          </Form.Item>

          <Form.Item
            name="description"
            label={intl.formatMessage({ id: 'gallery.form.description' }) || 'Description'}
          >
            <TextArea 
              rows={4} 
              placeholder={intl.formatMessage({ id: 'gallery.form.descriptionPlaceholder' }) || 'Enter gallery description'} 
            />
          </Form.Item>

          <Form.Item
            name="type"
            label={intl.formatMessage({ id: 'gallery.form.type' }) || 'Gallery Type'}
            rules={[{ required: true, message: intl.formatMessage({ id: 'gallery.form.typeRequired' }) || 'Please select gallery type' }]}
          >
            <Select placeholder={intl.formatMessage({ id: 'gallery.form.typePlaceholder' }) || 'Select gallery type'}>
              <Select.Option value={GalleryTypeEnum.General}>{intl.formatMessage({ id: 'gallery.type.general' }) || 'General'}</Select.Option>
              <Select.Option value={GalleryTypeEnum.Attendees}>{intl.formatMessage({ id: 'gallery.type.attendees' }) || 'Attendees'}</Select.Option>
              <Select.Option value={GalleryTypeEnum.VIP}>{intl.formatMessage({ id: 'gallery.type.vip' }) || 'VIP'}</Select.Option>
              <Select.Option value={GalleryTypeEnum.Staff}>{intl.formatMessage({ id: 'gallery.type.staff' }) || 'Staff'}</Select.Option>
              <Select.Option value={GalleryTypeEnum.Performers}>{intl.formatMessage({ id: 'gallery.type.performers' }) || 'Performers'}</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCancel}>
                {intl.formatMessage({ id: 'common.cancel' }) || 'Cancel'}
              </Button>
              <StyledButton type="primary" htmlType="submit" loading={isCreating}>
                {intl.formatMessage({ id: 'common.create' }) || 'Create'}
              </StyledButton>
            </Space>
          </Form.Item>
        </Form>
      </StyledModal>
      
      {/* Upgrade Modal */}
      <Modal
        open={isUpgradeModalOpen}
        onCancel={handleCloseUpgradeModal}
        footer={null}
        closable={false}
      >
        <UpgradeModal 
          onClose={handleCloseUpgradeModal} 
          featureName={intl.formatMessage({ id: 'features.maxGalleriesPerEvent.name' }) || 'Galleries Per Event'} 
          unlockLevel={intl.formatMessage(
            { id: 'organisation.plan.professional' },
            { plan: 'Professional' }
          ) || 'Professional Plan'}
        />
      </Modal>
    </StyledContent>
  );
};

export default EventDetailPage;